

import { useState, useEffect } from "react";
import Modal from "../../components/Modal";
import { eventsApi, clientsApi, mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Plus, Edit2 } from "lucide-react";

interface Client {
  id: number;
  name: string;
}

interface Package {
  id: number;
  name: string;
}

interface Venue {
  id: number;
  name: string;
}

interface Event {
  id: number;
  name: string;
  client: { id: number; name: string } | null;
  package: { id: number; name: string } | null;
  venue?: { id: number; name: string; location: string } | null;
  start_date: string;
  end_date: string | null;
  guest_count: number;
  status: string;
  payment_status?: string;
  event_type?: string;
  notes?: string;
  total_amount?: number;
}

interface EditEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEventUpdated: () => void;
  event: Event | null;
}

export default function EditEventModal({ isOpen, onClose, onEventUpdated, event }: EditEventModalProps) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [showAddClientForm, setShowAddClientForm] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);

  const [formData, setFormData] = useState({
    client_id: "",
    name: "",
    event_type: "",
    start_date: "",
    end_date: "",
    guest_count: "",
    package_id: "",
    venue_id: "",
    total_amount: "",
    status: "draft",
    payment_status: "unpaid",
    notes: "",
  });

  const [newClientData, setNewClientData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    if (isOpen && event) {
      loadDropdownData();
      populateFormData(event);
    }
  }, [isOpen, event]);

  const loadDropdownData = async () => {
    try {
      const [clientsRes, packagesRes, venuesRes, eventTypesRes] = await Promise.all([
        clientsApi.list(),
        mastersApi.packages.list(),
        mastersApi.venues.list(),
        mastersApi.eventTypes.list({ status: 'active' }),
      ]);

      setClients(clientsRes.data?.data || clientsRes.data || []);
      setPackages(packagesRes.data?.data || packagesRes.data || []);
      setVenues(venuesRes.data?.data || venuesRes.data || []);
      
      const etData = eventTypesRes.data?.data || eventTypesRes.data || [];
      setEventTypes(etData.map((t: any) => t.name));
    } catch (error) {
      console.error("Error loading dropdown data:", error);
    }
  };

  const populateFormData = (event: Event) => {
    setFormData({
      client_id: event.client?.id ? String(event.client.id) : "",
      name: event.name || "",
      event_type: event.event_type || "",
      start_date: event.start_date || "",
      end_date: event.end_date || "",
      guest_count: event.guest_count ? String(event.guest_count) : "",
      package_id: event.package?.id ? String(event.package.id) : "",
      venue_id: event.venue?.id ? String(event.venue.id) : "",
      total_amount: event.total_amount ? String(event.total_amount) : "",
      status: event.status || "draft",
      payment_status: event.payment_status || "unpaid",
      notes: event.notes || "",
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.client_id) newErrors.client_id = "Client is required";
    if (!formData.name.trim()) newErrors.name = "Event name is required";
    if (!formData.event_type) newErrors.event_type = "Event type is required";
    if (!formData.start_date) newErrors.start_date = "Event date is required";
    if (!formData.guest_count || parseInt(formData.guest_count) < 1) newErrors.guest_count = "Valid guest count is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !event) return;

    setLoading(true);
    try {
      const payload = {
        client_id: parseInt(formData.client_id),
        name: formData.name,
        event_type: formData.event_type,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        guest_count: formData.guest_count ? parseInt(formData.guest_count) : null,
        package_id: formData.package_id ? parseInt(formData.package_id) : null,
        venue_id: formData.venue_id ? parseInt(formData.venue_id) : null,
        total_amount: formData.total_amount ? parseFloat(formData.total_amount) : null,
        status: formData.status,
        payment_status: formData.payment_status,
        notes: formData.notes || null,
      };

      await eventsApi.update(event.id, payload);
      showToast("Event updated successfully", "success");
      onEventUpdated();
      onClose();
      resetForm();
    } catch (error) {
      showToast("Failed to update event", "error");
      console.error("Error updating event:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: "",
      name: "",
      event_type: "",
      start_date: "",
      end_date: "",
      guest_count: "",
      package_id: "",
      venue_id: "",
      total_amount: "",
      status: "draft",
      payment_status: "unpaid",
      notes: "",
    });
    setNewClientData({
      name: "",
      email: "",
      phone: "",
      address: "",
    });
    setShowAddClientForm(false);
    setErrors({});
  };

  const handleCreateClient = async () => {
    if (!newClientData.name.trim()) {
      showToast("Client name is required", "error");
      return;
    }

    setCreatingClient(true);
    try {
      const response = await clientsApi.create(newClientData);
      const newClient = response.data;
      
      // Add new client to the clients list
      setClients(prev => [...prev, newClient]);
      
      // Select the new client
      setFormData(prev => ({ ...prev, client_id: String(newClient.id) }));
      
      // Reset the add client form
      setNewClientData({
        name: "",
        email: "",
        phone: "",
        address: "",
      });
      setShowAddClientForm(false);
      
      showToast("Client created successfully", "success");
    } catch (error) {
      showToast("Failed to create client", "error");
      console.error("Error creating client:", error);
    } finally {
      setCreatingClient(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const inputClass = (field: string) => `
    w-full px-3 py-2.5 rounded-xl border text-sm transition-colors
    ${errors[field] 
      ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100" 
      : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
    }
    outline-none
  `;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Event" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Client Selection */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Client *</label>
            <div className="flex gap-2">
              <select
                value={formData.client_id}
                onChange={(e) => handleChange("client_id", e.target.value)}
                className={`${inputClass("client_id")} flex-1`}
              >
                <option value="">Select a client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowAddClientForm(!showAddClientForm)}
                className="px-3 py-2.5 rounded-xl bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
                title="Add New Client"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {errors.client_id && <p className="text-xs text-rose-500 mt-1">{errors.client_id}</p>}
          </div>

          {/* Add New Client Form */}
          {showAddClientForm && (
            <div className="sm:col-span-2 bg-stone-50 rounded-xl p-4 border border-stone-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-stone-800">Add New Client</h4>
                <button
                  type="button"
                  onClick={() => setShowAddClientForm(false)}
                  className="text-stone-400 hover:text-stone-600"
                >
                  ✕
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-stone-500 mb-1">Client Name *</label>
                  <input
                    type="text"
                    value={newClientData.name}
                    onChange={(e) => setNewClientData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter client name"
                    className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Email</label>
                  <input
                    type="email"
                    value={newClientData.email}
                    onChange={(e) => setNewClientData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="client@email.com"
                    className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={newClientData.phone}
                    onChange={(e) => setNewClientData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+91-XXXX-XXXX"
                    className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-stone-500 mb-1">Address</label>
                  <input
                    type="text"
                    value={newClientData.address}
                    onChange={(e) => setNewClientData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Client address"
                    className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                  />
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="button"
                    onClick={handleCreateClient}
                    disabled={creatingClient || !newClientData.name.trim()}
                    className="w-full px-4 py-2 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {creatingClient ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        Create Client
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Event Name */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Event Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Enter event name"
              className={inputClass("name")}
            />
            {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
          </div>

          {/* Event Type */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Event Type *</label>
            <select
              value={formData.event_type}
              onChange={(e) => handleChange("event_type", e.target.value)}
              className={inputClass("event_type")}
            >
              <option value="">Select event type</option>
              {eventTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            {errors.event_type && <p className="text-xs text-rose-500 mt-1">{errors.event_type}</p>}
          </div>

          {/* Event Date */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Event Date *</label>
            <input
              type="date"
              value={formData.start_date}
              onChange={(e) => handleChange("start_date", e.target.value)}
              className={inputClass("start_date")}
            />
            {errors.start_date && <p className="text-xs text-rose-500 mt-1">{errors.start_date}</p>}
          </div>

          {/* End Date */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">End Date (Optional)</label>
            <input
              type="date"
              value={formData.end_date}
              onChange={(e) => handleChange("end_date", e.target.value)}
              className={inputClass("end_date")}
              min={formData.start_date}
            />
          </div>

          {/* Guest Count */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Number of Guests *</label>
            <input
              type="number"
              min="1"
              value={formData.guest_count}
              onChange={(e) => handleChange("guest_count", e.target.value)}
              placeholder="e.g., 150"
              className={inputClass("guest_count")}
            />
            {errors.guest_count && <p className="text-xs text-rose-500 mt-1">{errors.guest_count}</p>}
          </div>

          {/* Package */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Package (Optional)</label>
            <select
              value={formData.package_id}
              onChange={(e) => handleChange("package_id", e.target.value)}
              className={inputClass("package_id")}
            >
              <option value="">Select package</option>
              {packages.map((pkg) => (
                <option key={pkg.id} value={pkg.id}>
                  {pkg.name}
                </option>
              ))}
            </select>
          </div>

          {/* Venue */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Venue (Optional)</label>
            <select
              value={formData.venue_id}
              onChange={(e) => handleChange("venue_id", e.target.value)}
              className={inputClass("venue_id")}
            >
              <option value="">Select venue</option>
              {venues.map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {venue.name}
                </option>
              ))}
            </select>
          </div>

          {/* Total Amount */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Total Amount (₹)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.total_amount}
              onChange={(e) => handleChange("total_amount", e.target.value)}
              placeholder="0.00"
              className={inputClass("total_amount")}
            />
            {errors.total_amount && <p className="text-xs text-rose-500 mt-1">{errors.total_amount}</p>}
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Status</label>
            <select
              value={formData.status}
              onChange={(e) => handleChange("status", e.target.value)}
              className={inputClass("status")}
            >
              <option value="draft">Draft</option>
              <option value="quoted">Quoted</option>
              <option value="confirmed">Confirmed</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Payment Status */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Payment Status</label>
            <select
              value={formData.payment_status}
              onChange={(e) => handleChange("payment_status", e.target.value)}
              className={inputClass("payment_status")}
            >
              <option value="unpaid">Unpaid</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>

          {/* Notes */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Additional notes..."
              rows={3}
              className={`${inputClass("notes")} resize-none`}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-stone-100">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Updating...
              </>
            ) : (
              <>
                <Edit2 className="w-4 h-4" />
                Update Event
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
